<template>
  <el-row :gutter="16">
    <el-col :span="12">
      <PDatePicker
        type="date"
        v-model="date"
        :disabled="selecionarPeriodo"
        :error="!selecionarPeriodo && !!errors?.date"
        placeholder="Selecione uma data"
        label="Data"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" style="margin-top: 16px">
      <PSelect
        v-model="tipoArquivo"
        placeholder="Selecione um tipo de arquivo"
        label="Tipo de arquivo"
        :options="TipoArquivoMoozCartoesOpt"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="24" style="margin: 0.7rem 0">
      <PCheckbox label="Selecionar um período" v-model="selecionarPeriodo" />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="inicio"
        :error="selecionarPeriodo && !inicio"
        placeholder="Selecione uma data de início"
        label="Início"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" v-if="selecionarPeriodo" style="margin-bottom: 16px">
      <PDatePicker
        type="date"
        v-model="fim"
        :error="selecionarPeriodo && !fim"
        placeholder="Selecione uma data de fim"
        label="Fim"
        style="width: 100%"
        clearable
      />
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { DITypes } from "@/core/@types/types";
import { PCheckbox, PDatePicker, PSelect } from "@f360-dev/f360-prisma";
import { TWebservice } from "../../../../services/PainelDeIntegracaoesGateway";
import { TAPIRules } from "../../../../../../@types/IntegracoesAPIRules";
import { diContainer } from "@/inversify.config";
import { IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import {
  formMoozCartoesSchema,
  IFormularioMoozCartoesSchema
} from "./formularioMoozCartoesSchema";
import { useForm } from "vee-validate";
import { computed, onMounted } from "vue";
import {
  ETipoArquivoMoozCartoes,
  TipoArquivoMoozCartoesOpt
} from "../../../../../../@types/IntegracoesAPIEnum";

const props = defineProps<{
  webservice?: TWebservice;
  empresasOpt: { label: string; value: string }[];
  apiSelecionada?: TAPIRules;
}>();

const financasFeatureGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const { handleSubmit, errors, setValues, values, defineField } =
  useForm<IFormularioMoozCartoesSchema>({
    validationSchema: formMoozCartoesSchema,
    initialValues: {
      date: new Date().toISOString().split("T")[0],
      empresa: "",
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoArquivo: ETipoArquivoMoozCartoes.Ambos.toString()
    }
  });

const [date] = defineField("date");
const [selecionarPeriodo] = defineField("selecionarPeriodo");
const [inicio] = defineField("inicio");
const [fim] = defineField("fim");
const [tipoArquivo] = defineField("tipoArquivo");

const periodoUltrapassaLimiteDaApi = (): boolean => {
  const limite = props.apiSelecionada?.intervaloData;
  if (limite == null || limite <= 0) return false;

  const dataInicio = inicio.value != null ? new Date(inicio.value) : null;
  const dataFim = fim.value != null ? new Date(fim.value) : null;

  if (!dataInicio || !dataFim) return false;

  const diffEmDiasInicio = dataInicio.getDate();
  const diffEmDiasFim = dataFim.getDate();
  const diffEmDias = diffEmDiasFim - diffEmDiasInicio;

  if (diffEmDias < 0) return true;

  return diffEmDias >= limite;
};

const disabledSubmit = computed(() => {
  const missingDate = !date.value || String(date.value).trim() === "";
  const missingTipoArquivo =
    !tipoArquivo.value || String(tipoArquivo.value).trim() === "";

  if (missingDate || missingTipoArquivo) return true;

  if (selecionarPeriodo.value) {
    const missingInicio =
      inicio.value == null || String(inicio.value).trim() === "";
    const missingFim = fim.value == null || String(fim.value).trim() === "";

    return missingInicio || missingFim || periodoUltrapassaLimiteDaApi();
  }

  return false;
});

const obterEmpresaPorConfiguracao = async () => {
  if (!financasFeatureGateway) return;

  const response = await financasFeatureGateway.obterEmpresaPorConfiguracao(
    props.webservice?.id ?? ""
  );

  if (response?.Ok) {
    setValues({
      date: new Date().toISOString().split("T")[0],
      empresa: response.Result,
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoArquivo: ETipoArquivoMoozCartoes.Ambos.toString()
    });
  }
};

onMounted(async () => {
  if (props.apiSelecionada?.requiresCompanyLookup) {
    try {
      await obterEmpresaPorConfiguracao();
    } catch (error) {
      console.error(error);
    }
  } else {
    setValues({
      date: new Date().toISOString().split("T")[0],
      empresa: props.webservice?.empresa ?? "",
      selecionarPeriodo: false,
      inicio: null,
      fim: null,
      tipoArquivo: ETipoArquivoMoozCartoes.Ambos.toString()
    });
  }
});

defineExpose({
  handleSubmit,
  errors,
  values,
  disabledSubmit
});
</script>

<style lang="scss" scoped></style>
